import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/hooks/useAuth";
import { useIsTablet } from "@/hooks/use-mobile";
import { PageHeader } from "@/components/layout/PageHeaderSlot";
import { adminApi, type Menu } from "@/lib/api/admin-api";
import { useLingui } from "@lingui/react";

import {
  UtensilsCrossed,
  Plus,
  Search,
  Eye,
  Edit,
  Trash2,
  RefreshCw,
} from "lucide-react";
import { format } from "date-fns";
import { Skeleton } from "@/components/ui/skeleton";
import { Trans } from "@lingui/react/macro";

export const Route = createFileRoute("/(protected)/_layout/admin/menus")({
  component: MenusPage,
});

function MenusPage() {
  const { i18n } = useLingui();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const isTablet = useIsTablet();
  const [searchQuery, setSearchQuery] = useState("");

  const {
    data: menusResponse,
    isLoading,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ["admin", "menus"],
    queryFn: adminApi.getMenus,
    enabled: isAuthenticated,
  });

  if (authLoading || !isAuthenticated || isLoading) {
    return <AdminMenusSkeleton />;
  }

  const menus: Menu[] = Array.isArray(menusResponse)
    ? menusResponse
    : (menusResponse as unknown as { data?: Menu[] })?.data || [];

  const filteredMenus = menus.filter(
    (menu: Menu) =>
      menu.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      menu.type.toLowerCase().includes(searchQuery.toLowerCase()) ||
      menu.brand?.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const getTypeVariant = (type: string) => {
    switch (type) {
      case "BREAKFAST":
        return "warning";
      case "LUNCH":
        return "default";
      case "DINNER":
        return "secondary";
      case "ALL_DAY":
        return "success";
      case "SEASONAL":
        return "outline";
      case "CATERING":
        return "destructive";
      default:
        return "default";
    }
  };

  const addMenuButton = (
    <Button className="gap-2">
      <Plus className="h-4 w-4" />
      <Trans>Add Menu</Trans>
    </Button>
  );

  return (
    <div className="space-y-6">
      {isTablet ? (
        <PageHeader>
          <div className="flex h-15.5 items-center gap-3 border-b border-(--color-border) px-5">
            <UtensilsCrossed className="h-6 w-6 shrink-0 text-(--color-warning-text)" />
            <h1 className="text-[1.375rem] font-extrabold tracking-tight leading-none text-(--color-text-primary) truncate">
              {i18n._("Menus")}
            </h1>
            <div className="ml-auto flex items-center gap-2 shrink-0">
              {addMenuButton}
            </div>
          </div>
        </PageHeader>
      ) : (
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
              <UtensilsCrossed className="h-8 w-8 text-(--color-warning-text)" />
              {i18n._("Menus")}
            </h1>
            <p className="text-(--color-text-secondary) mt-1">
              {i18n._("Manage restaurant menus")}
            </p>
          </div>
          {addMenuButton}
        </div>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{i18n._("All Menus")}</CardTitle>
              <CardDescription>
                {i18n._(
                  "{count, plural, one {# menu found} other {# menus found}}",
                  { count: filteredMenus.length },
                )}
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative w-64">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-(--color-text-secondary)" />
                <Input
                  placeholder={i18n._("Search menus...")}
                  className="pl-8"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <Button
                variant="outline"
                size="icon"
                onClick={() => refetch()}
                disabled={isFetching}
                title={i18n._("Refresh")}
              >
                <RefreshCw
                  className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`}
                />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{i18n._("Name")}</TableHead>
                <TableHead>{i18n._("Brand")}</TableHead>
                <TableHead>{i18n._("Type")}</TableHead>
                <TableHead>{i18n._("Items")}</TableHead>
                <TableHead>{i18n._("Date Range")}</TableHead>
                <TableHead>{i18n._("Status")}</TableHead>
                <TableHead>{i18n._("Created")}</TableHead>
                <TableHead className="text-right">
                  {i18n._("Actions")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredMenus.length > 0 ? (
                filteredMenus.map((menu: Menu) => (
                  <TableRow key={menu.id}>
                    <TableCell className="font-medium">{menu.name}</TableCell>
                    <TableCell className="text-(--color-text-secondary)">
                      {menu.brand?.name ?? i18n._("All")}
                    </TableCell>
                    <TableCell>
                      <Badge variant={getTypeVariant(menu.type)}>
                        {menu.type.replace("_", " ")}
                      </Badge>
                    </TableCell>
                    <TableCell>{menu._count?.items ?? 0}</TableCell>
                    <TableCell>
                      {menu.startDate && menu.endDate ? (
                        <span className="text-sm">
                          {format(new Date(menu.startDate), "MMM d")} -{" "}
                          {format(new Date(menu.endDate), "MMM d, yyyy")}
                        </span>
                      ) : (
                        <span className="text-(--color-text-secondary)">{i18n._("Always")}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={menu.isActive ? "success" : "secondary"}>
                        {menu.isActive ? i18n._("Active") : i18n._("Inactive")}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {format(new Date(menu.createdAt), "MMM d, yyyy")}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="ghost" size="icon">
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon">
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-(--color-danger-text)"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    className="text-center text-(--color-text-secondary) py-8"
                  >
                    {i18n._("No menus found")}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function AdminMenusSkeleton() {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Skeleton className="h-8 w-40" />
          <Skeleton className="mt-2 h-4 w-52" />
        </div>
        <Skeleton className="h-9 w-28" />
      </div>
      <div className="rounded-lg border">
        <div className="flex items-center justify-between p-4">
          <div>
            <Skeleton className="h-5 w-32" />
            <Skeleton className="mt-2 h-4 w-44" />
          </div>
          <div className="flex items-center gap-2">
            <Skeleton className="h-9 w-56" />
            <Skeleton className="h-9 w-9" />
          </div>
        </div>
        <div className="p-4 space-y-3">
          {Array.from({ length: 6 }).map((_, index) => (
            <Skeleton key={index} className="h-10 w-full" />
          ))}
        </div>
      </div>
    </div>
  );
}
